import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import type { QuestionAnswer } from "#types/types.ts";
import { type ColorValue, Div, Flex, Text, useColorSchemeValue } from "@hopper-ui/components";
import { CheckmarkIcon } from "@hopper-ui/icons";
import "./InteractiveQuestionResponseMessage.css";

interface Props {
    answers: QuestionAnswer[];
}

export function InteractiveQuestionResponseMessage({ answers }: Props) {
    const { tShared } = useSharedTranslation();
    const answerOccurrences = new Map<string, number>();
    const textColor = useColorSchemeValue(
        "neutral-strong",
        "core_orchid-bloom-75"
    ) as ColorValue;
    const backgroundColor = useColorSchemeValue(
        "var(--hop-orchid-bloom-600)",
        "linear-gradient(92deg, #5454be 0%, #322b8d 100%)"
    );

    return (
        <Flex justifyContent="end" className="wai-interactive-question-response">
            <Div
                borderRadius="rounded-lg"
                paddingY="inset-sm"
                paddingX="inset-md"
                marginLeft={{ base: "core_0", sm: "core_320" }}
                UNSAFE_maxWidth="32rem"
                UNSAFE_width="fit-content"
                color={textColor}
                style={{ background: backgroundColor }}
                className="wai-interactive-question-response-content"
            >
                <Flex alignItems="center" gap="inline-xs" marginBottom="inline-sm" color="inherit" opacity={0.78}>
                    <CheckmarkIcon size="sm" />
                    <Text size="xs" fontWeight="body-xs-medium" color="inherit">
                        {tShared("interactiveQuestion.responseTitle", { count: answers.length })}
                    </Text>
                </Flex>
                <Flex direction="column" gap="inline-sm">
                    {answers.map(({ question, answer }) => {
                        const answerText = answer.join(", ");
                        const answerKey = JSON.stringify([question, answerText]);
                        const occurrence = answerOccurrences.get(answerKey) ?? 0;
                        answerOccurrences.set(answerKey, occurrence + 1);

                        return (
                            <Flex key={`${answerKey}:${occurrence}`} direction="column" gap="inline-xs">
                                <Text size="xs" color="inherit" opacity={0.78}>{question}</Text>
                                <Text size="sm" color="inherit">{answerText}</Text>
                            </Flex>
                        );
                    })}
                </Flex>
            </Div>
        </Flex>
    );
}
